import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { buildVenueMapItem } from './AdminGeoMapExplorer';
import { AdminDetailSection, Badge, Card, adminDetailFieldCardStyle } from './AdminPanelPrimitives';

const copyInviteVenueText = async (text) => {
  if (!text) return false;
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // continue to fallback
    }
  }
  window.prompt('Copy this invite', text);
  return false;
};

const resolveAssetUrl = (url, backendUrl) => {
  if (!url) return '';
  return url.startsWith('/') ? `${backendUrl}${url}` : url;
};

const APPROVALS_COLUMN_HEIGHT = 'calc(100vh - 230px)';

const FIELD_ISSUE_CATEGORIES = [
  { id: 'contact', label: 'Contact details', placeholder: 'e.g. Owner name spelled wrong' },
  { id: 'business', label: 'Business / VAT', placeholder: "e.g. Legal name doesn't match VAT registration" },
  { id: 'address', label: 'Address & location', placeholder: 'e.g. Street number typo / wrong pin' },
  { id: 'hours', label: 'Operating hours', placeholder: 'e.g. Closed on Mondays — please update' },
  { id: 'social', label: 'Social media', placeholder: 'e.g. Instagram handle 404s' },
  { id: 'type', label: 'Venue type & categories', placeholder: 'e.g. Listed as Cafe but actually a Restaurant' },
  { id: 'other', label: 'Other details', placeholder: 'Anything else owner needs to fix' },
];

export function ApprovalsDetailView({
  selectedVenueApp,
  onBack,
  approveVenueApplication,
  venueAction,
  onContactUser,
  onApproveReferral,
  onMarkReferralInviteSent,
  onRejectReferral,
  onRemoveReferral,
  onRequestMediaChanges,
  backendUrl,
  focusReferredHost,
  focusPendingMapItem,
}) {
  const [photosMarkedForRemoval, setPhotosMarkedForRemoval] = useState([]);
  const [videoMarkedForRemoval, setVideoMarkedForRemoval] = useState(false);
  const [mediaChangeNote, setMediaChangeNote] = useState('');
  const [mediaChangeSubmitting, setMediaChangeSubmitting] = useState(false);
  const [fieldIssueDrafts, setFieldIssueDrafts] = useState({});

  useEffect(() => {
    setPhotosMarkedForRemoval([]);
    setVideoMarkedForRemoval(false);
    setMediaChangeNote('');
    setFieldIssueDrafts({});
  }, [selectedVenueApp?.id]);

  const activeFieldIssues = FIELD_ISSUE_CATEGORIES
    .filter((cat) => fieldIssueDrafts[cat.id]?.enabled)
    .map((cat) => ({ category: cat.id, note: (fieldIssueDrafts[cat.id]?.note || '').trim() }));

  const anyRevisionFlagged = photosMarkedForRemoval.length > 0 || videoMarkedForRemoval || activeFieldIssues.length > 0;

  const isReferredDetail = selectedVenueApp.sourceType === 'referred';

  return (
    <div style={{ maxHeight: APPROVALS_COLUMN_HEIGHT, overflowY: 'auto', paddingRight: 6 }}>
      <style>{`@keyframes admin-referral-pulse { 0% { box-shadow: 0 0 0 0 rgba(23, 138, 47, 0.18), 0 10px 28px rgba(23, 138, 47, 0.08); } 70% { box-shadow: 0 0 0 10px rgba(23, 138, 47, 0), 0 10px 28px rgba(23, 138, 47, 0.16); } 100% { box-shadow: 0 0 0 0 rgba(23, 138, 47, 0), 0 10px 28px rgba(23, 138, 47, 0.08); } }`}</style>
      <button type="button" onClick={onBack} data-testid="admin-approving-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#2F79EA', marginBottom: 12, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>← Back to approvals</button>
      {isReferredDetail ? (
        <>
          <Card style={{ marginBottom: 12 }}>
            <p data-testid="admin-approving-detail-title" style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700 }}>{selectedVenueApp.restaurant_name || 'Referred venue'}</p>
            <p data-testid="admin-approving-detail-address" style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{selectedVenueApp.venue_address || 'Address unavailable'}</p>
            <p data-testid="admin-approving-detail-host-name" style={{ margin: '8px 0 0', fontSize: 13, color: '#111' }}>Host: {selectedVenueApp.referred_by_name || selectedVenueApp.user?.name || 'Host'}</p>
            <p data-testid="admin-approving-detail-host-email" style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{selectedVenueApp.referred_by_email || selectedVenueApp.user?.email || '—'}</p>
            <p data-testid="admin-approving-detail-host-account-id" style={{ margin: '8px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#111' }}>Host Account ID: {selectedVenueApp.user?.customer_id || '—'}</p>
            {selectedVenueApp.invitationAcceptedAt ? <p data-testid="admin-approving-detail-invite-accepted" style={{ margin: '8px 0 0', fontSize: 13, color: '#178A2F', fontWeight: 700 }}>Invitation was accepted</p> : null}
            {selectedVenueApp.coverageHint ? <p data-testid="admin-approving-detail-coverage-hint" style={{ margin: '8px 0 0', fontSize: 13, color: '#2F79EA', lineHeight: 1.35 }}>{selectedVenueApp.coverageHint}</p> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              {selectedVenueApp.referrer_id ? <button type="button" onClick={() => {
                onBack();
                focusReferredHost({
                  id: selectedVenueApp.id,
                  referrer_id: selectedVenueApp.referrer_id,
                  referrer_user: selectedVenueApp.user,
                });
              }} data-testid="admin-approving-detail-view-host-map" style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', fontWeight: 700 }}>View host on map</button> : null}
              <button type="button" onClick={() => toast.info('Host listings are shown below in this detail view.')} data-testid="admin-approving-detail-view-host-listings" style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', fontWeight: 700 }}>View host’s listings</button>
              {selectedVenueApp.status === 'approved' ? (
                <>
                  <button type="button" onClick={async () => {
                    const copied = await copyInviteVenueText(selectedVenueApp.inviteMessage);
                    await onMarkReferralInviteSent(selectedVenueApp.id);
                    toast.success(copied ? 'Venue invite copied' : 'Copy prompt opened');
                  }} data-testid="admin-approving-detail-invite-venue" style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: '#2F79EA', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Invite Venue</button>
                  <button type="button" onClick={() => onRemoveReferral(selectedVenueApp.id)} data-testid="admin-approving-detail-remove-button" style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid rgba(221, 6, 8, 0.18)', background: '#FFFFFF', color: '#DD0608', cursor: 'pointer', fontWeight: 700 }}>Remove</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => onApproveReferral(selectedVenueApp.id)} data-testid="admin-approving-detail-approve-button" style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: '#00C853', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Approve</button>
                  <button type="button" onClick={() => onRejectReferral(selectedVenueApp.id)} data-testid="admin-approving-detail-decline-button" style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: '#DD0608', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Decline</button>
                </>
              )}
              <button type="button" onClick={() => onContactUser(selectedVenueApp.user?.id || selectedVenueApp.referrer_id)} data-testid="admin-approving-detail-contact-host" style={{ marginLeft: 'auto', padding: '10px 14px', borderRadius: 12, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 700 }}>Contact host</button>
            </div>
          </Card>
          <Card style={{ marginBottom: 12 }}>
            <p style={{ margin: '0 0 12px', fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700 }}>Referral details</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <AdminDetailSection
                dataTestId="admin-referred-host-details-section"
                title="Host details"
                items={[
                  { label: 'Host', value: selectedVenueApp.referred_by_name || selectedVenueApp.user?.name || '—' },
                  { label: 'E-mail', value: selectedVenueApp.referred_by_email || selectedVenueApp.user?.email || '—' },
                  { label: 'Account ID', value: selectedVenueApp.user?.customer_id || '—' },
                ]}
              />
              <AdminDetailSection
                dataTestId="admin-referred-host-listings-section"
                title="Host listings"
              >
                {(selectedVenueApp.hostListings || []).length ? (selectedVenueApp.hostListings || []).map((listing) => (
                  <div key={listing.id} style={adminDetailFieldCardStyle}>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#8A839F', textTransform: 'uppercase', letterSpacing: 0.4 }}>{listing.listing_title || 'Listing'}</p>
                    <p style={{ margin: '5px 0 0', fontSize: 14, fontWeight: 600, color: '#111', lineHeight: 1.4 }}>{`${listing.listing_location_label || 'Location unavailable'} · ${listing.status || 'pending'}`}</p>
                  </div>
                )) : <div style={adminDetailFieldCardStyle}><p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111' }}>No host listings found</p></div>}
              </AdminDetailSection>
              <AdminDetailSection
                dataTestId="admin-referred-venue-info-section"
                title="Venue info provided by host"
                items={[
                  { label: 'Venue', value: selectedVenueApp.restaurant_name || '—' },
                  { label: 'Type', value: selectedVenueApp.venue_type || selectedVenueApp.venue_category || '—' },
                  { label: 'Location', value: selectedVenueApp.venue_address || '—' },
                  { label: 'Website / Instagram', value: selectedVenueApp.venue_link || '—' },
                ]}
              />
              {selectedVenueApp.reason ? (
                <AdminDetailSection dataTestId="admin-referred-reason-section" title="Why this host recommends it">
                  <div style={adminDetailFieldCardStyle}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111', lineHeight: 1.5 }}>{selectedVenueApp.reason}</p>
                  </div>
                </AdminDetailSection>
              ) : null}
            </div>
          </Card>
        </>
      ) : (
        <>
          <Card style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div>
                <p data-testid="admin-approving-detail-title" style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700 }}>{selectedVenueApp.restaurant_name || 'Pending venue application'}</p>
                <p data-testid="admin-approving-detail-address" style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{[selectedVenueApp.street, selectedVenueApp.city, selectedVenueApp.region, selectedVenueApp.country].filter(Boolean).join(', ') || [selectedVenueApp.city, selectedVenueApp.country].filter(Boolean).join(', ') || 'Address unavailable'}</p>
                <p data-testid="admin-approving-detail-owner-name" style={{ margin: '8px 0 0', fontSize: 13, color: '#111' }}>Venue owner: {[selectedVenueApp.first_name, selectedVenueApp.last_name].filter(Boolean).join(' ') || selectedVenueApp.user?.name || '—'}</p>
                <p data-testid="admin-approving-detail-owner-email" style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{selectedVenueApp.contact_email || selectedVenueApp.user?.email || '—'}</p>
                <p data-testid="admin-approving-detail-owner-account-id" style={{ margin: '8px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#111' }}>Owner Account ID: {selectedVenueApp.user?.customer_id || '—'}</p>
              </div>
              <Badge text={`Step ${selectedVenueApp.current_step || '—'}/7`} color="blue" />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => {
                onBack();
                focusPendingMapItem(selectedVenueApp, true);
              }} data-testid="admin-approving-detail-view-venue-map" style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', fontWeight: 700 }}>{buildVenueMapItem(selectedVenueApp) ? 'View on map' : 'Map unavailable'}</button>
              <button type="button" onClick={() => approveVenueApplication(selectedVenueApp.id)} data-testid="admin-approving-detail-approve-button" style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: '#00C853', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Approve</button>
              <button type="button" onClick={() => venueAction(selectedVenueApp.id, 'reject')} data-testid="admin-approving-detail-decline-button" style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: '#DD0608', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>Decline</button>
              <button type="button" onClick={() => onContactUser(selectedVenueApp.user?.id || selectedVenueApp.user_id)} data-testid="admin-approving-detail-contact-owner" style={{ marginLeft: 'auto', padding: '10px 14px', borderRadius: 12, border: '1px solid rgba(112, 63, 246, 0.14)', background: '#FFFFFF', color: '#2F145F', cursor: 'pointer', fontWeight: 700 }}>Contact owner</button>
            </div>
          </Card>

          <Card style={{ marginBottom: 12 }}>
            <p style={{ margin: '0 0 12px', fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700 }}>Venue application details</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <AdminDetailSection
                dataTestId="admin-pending-contact-details-section"
                title="Contact details"
                items={[
                  { label: 'Name', value: [selectedVenueApp.first_name, selectedVenueApp.last_name].filter(Boolean).join(' ') },
                  { label: 'Phone', value: [selectedVenueApp.phone_country_code, selectedVenueApp.phone].filter(Boolean).join(' ') },
                  { label: 'Email', value: selectedVenueApp.contact_email },
                ]}
              />
              <AdminDetailSection
                dataTestId="admin-pending-business-details-section"
                title="Business details"
                items={[
                  { label: 'Restaurant', value: selectedVenueApp.restaurant_name },
                  { label: 'Legal name', value: selectedVenueApp.legal_business_name },
                  { label: 'VAT', value: selectedVenueApp.business_vat },
                ]}
              />
              <AdminDetailSection
                dataTestId="admin-pending-address-section"
                title="Address"
                items={[
                  { label: 'Country', value: selectedVenueApp.country },
                  { label: 'Region', value: selectedVenueApp.region },
                  { label: 'City', value: selectedVenueApp.city },
                  { label: 'Street', value: selectedVenueApp.street },
                  { label: 'Zip', value: selectedVenueApp.zip_code },
                  { label: 'Coordinates', value: selectedVenueApp.coordinates },
                ]}
              />
              <AdminDetailSection
                dataTestId="admin-pending-social-media-section"
                title="Social media"
                items={[
                  { label: 'Website', value: selectedVenueApp.website },
                  { label: 'Instagram', value: selectedVenueApp.instagram },
                  { label: 'Facebook', value: selectedVenueApp.facebook },
                  { label: 'TikTok', value: selectedVenueApp.tiktok },
                ]}
              />
              <AdminDetailSection
                dataTestId="admin-pending-venue-setup-section"
                title="Venue setup"
                items={[
                  { label: 'Type', value: selectedVenueApp.venue_type },
                  { label: 'Customer phone', value: selectedVenueApp.customer_phone },
                  { label: 'Current step', value: selectedVenueApp.current_step ? `${selectedVenueApp.current_step}/7` : '' },
                ]}
              />
              <AdminDetailSection
                dataTestId="admin-pending-owner-account-section"
                title="Owner account"
                items={[
                  { label: 'Name', value: selectedVenueApp.user?.name },
                  { label: 'Email', value: selectedVenueApp.user?.email },
                  { label: 'Role', value: selectedVenueApp.user?.role },
                  { label: 'Verified', value: selectedVenueApp.user?.email_verified ? 'Yes' : 'No' },
                ]}
              />
            </div>
          </Card>

          <Card style={{ marginBottom: 12 }}>
            <p style={{ margin: '0 0 12px', fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700 }}>Operating hours</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
              {selectedVenueApp.operating_hours && Object.entries(selectedVenueApp.operating_hours).length ? Object.entries(selectedVenueApp.operating_hours).map(([day, hours]) => {
                const hoursText = typeof hours === 'object' && hours !== null
                  ? [hours.open, hours.close].filter(Boolean).join(' – ') || JSON.stringify(hours)
                  : String(hours || '—');
                return (
                  <div key={day} style={adminDetailFieldCardStyle}>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: '#8A839F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{day}</p>
                    <p style={{ margin: '6px 0 0', fontSize: 14, fontWeight: 600, color: '#111' }}>{hoursText}</p>
                  </div>
                );
              }) : <p style={{ margin: 0, fontSize: 13, color: '#666' }}>No operating hours added yet.</p>}
            </div>
          </Card>

          <Card>
            <p style={{ margin: '0 0 12px', fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700 }}>Send for revision</p>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#666', lineHeight: 1.45 }}>
              Flag media to be removed, or pick venue-info fields the owner needs to edit. They’ll get a single in-app notification listing every fix, the application returns to draft, and they pick up at the right step.
            </p>

            <div style={{ marginBottom: 18 }}>
              <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#111' }}>Intro video</p>
              <div
                data-testid="admin-approving-detail-video-muted-reminder"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: 'rgba(47, 121, 234, 0.08)',
                  border: '1px solid rgba(47, 121, 234, 0.18)',
                  color: '#1F4FA3',
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 10,
                  maxWidth: 480,
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 14 }}>🔇</span>
                Guests view this video <strong style={{ fontWeight: 800 }}>muted</strong> in the app — audio quality isn’t a valid reason to request changes.
              </div>
              {selectedVenueApp.intro_video_url ? (
                (() => {
                  const videoSrc = resolveAssetUrl(selectedVenueApp.intro_video_url, backendUrl);
                  const isMarked = videoMarkedForRemoval;
                  return (
                    <div
                      data-testid="admin-approving-detail-video-block"
                      style={{
                        position: 'relative',
                        borderRadius: 16,
                        overflow: 'hidden',
                        border: isMarked ? '2px solid #DD0608' : '1px solid var(--card-border)',
                        boxShadow: 'var(--premium-shadow-nested)',
                        maxWidth: 480,
                      }}
                    >
                      <video
                        data-testid="admin-approving-detail-video"
                        src={videoSrc}
                        controls
                        playsInline
                        preload="metadata"
                        style={{ width: '100%', display: 'block', background: '#000', aspectRatio: '16/9', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        onClick={() => setVideoMarkedForRemoval((current) => !current)}
                        data-testid="admin-approving-detail-video-toggle"
                        style={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          padding: '6px 10px',
                          borderRadius: 9999,
                          border: 'none',
                          background: isMarked ? '#DD0608' : 'rgba(17, 17, 17, 0.85)',
                          color: '#FFF',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 12,
                          backdropFilter: 'blur(6px)',
                        }}
                      >
                        {isMarked ? 'Marked for removal · undo' : 'Mark for removal'}
                      </button>
                    </div>
                  );
                })()
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: '#666' }}>No intro video uploaded.</p>
              )}
            </div>

            <div>
              <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#111' }}>Photos ({(selectedVenueApp.photo_urls || []).length})</p>
              {(selectedVenueApp.photo_urls || []).length ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                  {(selectedVenueApp.photo_urls || []).map((url, index) => {
                    const isMarked = photosMarkedForRemoval.includes(url);
                    return (
                      <div
                        key={`${selectedVenueApp.id}-${url}`}
                        style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', border: isMarked ? '2px solid #DD0608' : '1px solid rgba(112, 63, 246, 0.1)', boxShadow: 'var(--premium-shadow-nested)' }}
                      >
                        <img
                          src={resolveAssetUrl(url, backendUrl)}
                          alt={`Venue submission ${index + 1}`}
                          data-testid={`admin-approving-detail-photo-${index}`}
                          style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block', filter: isMarked ? 'grayscale(0.6) brightness(0.7)' : 'none' }}
                        />
                        <button
                          type="button"
                          onClick={() => setPhotosMarkedForRemoval((current) => (
                            current.includes(url) ? current.filter((entry) => entry !== url) : [...current, url]
                          ))}
                          data-testid={`admin-approving-detail-photo-toggle-${index}`}
                          aria-label={isMarked ? 'Undo removal' : 'Mark photo for removal'}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            border: 'none',
                            background: isMarked ? '#DD0608' : 'rgba(17, 17, 17, 0.85)',
                            color: '#FFF',
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: 16,
                            lineHeight: '28px',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {isMarked ? '↺' : '×'}
                        </button>
                        {isMarked ? (
                          <span style={{ position: 'absolute', bottom: 8, left: 8, padding: '2px 8px', borderRadius: 9999, background: '#DD0608', color: '#FFF', fontSize: 10, fontWeight: 800, letterSpacing: 0.4 }}>REMOVING</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : <p style={{ margin: 0, fontSize: 13, color: '#666' }}>No photos uploaded.</p>}
            </div>

            <div style={{ marginTop: 22 }} data-testid="admin-approving-detail-field-issues-section">
              <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#111' }}>Venue info to revise</p>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#666', lineHeight: 1.45 }}>
                Pick the areas the owner needs to edit. Notes here are sent verbatim — be specific.
              </p>
              <div style={{ display: 'grid', gap: 8 }}>
                {FIELD_ISSUE_CATEGORIES.map((cat) => {
                  const draft = fieldIssueDrafts[cat.id] || { enabled: false, note: '' };
                  return (
                    <div
                      key={cat.id}
                      data-testid={`admin-approving-detail-field-issue-${cat.id}`}
                      style={{
                        borderRadius: 12,
                        border: draft.enabled ? '1px solid rgba(47, 121, 234, 0.45)' : '1px solid #E7E7E7',
                        background: draft.enabled ? 'rgba(47, 121, 234, 0.04)' : '#FFF',
                        padding: '10px 12px',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#111' }}>
                        <input
                          type="checkbox"
                          data-testid={`admin-approving-detail-field-issue-${cat.id}-checkbox`}
                          checked={draft.enabled}
                          onChange={(event) => setFieldIssueDrafts((current) => ({
                            ...current,
                            [cat.id]: { enabled: event.target.checked, note: current[cat.id]?.note || '' },
                          }))}
                          style={{ width: 16, height: 16, accentColor: '#2F79EA', cursor: 'pointer' }}
                        />
                        {cat.label}
                      </label>
                      {draft.enabled ? (
                        <textarea
                          value={draft.note}
                          onChange={(event) => setFieldIssueDrafts((current) => ({
                            ...current,
                            [cat.id]: { enabled: true, note: event.target.value },
                          }))}
                          placeholder={cat.placeholder}
                          data-testid={`admin-approving-detail-field-issue-${cat.id}-note`}
                          style={{ marginTop: 8, width: '100%', minHeight: 56, padding: 8, borderRadius: 8, border: '1px solid #E7E7E7', fontFamily: 'inherit', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {anyRevisionFlagged ? (
              <div
                data-testid="admin-approving-detail-media-change-panel"
                style={{ marginTop: 18, padding: 14, borderRadius: 14, background: 'rgba(221, 6, 8, 0.04)', border: '1px solid rgba(221, 6, 8, 0.18)' }}
              >
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111' }}>Sending for revision</p>
                <ul data-testid="admin-approving-detail-revision-summary" style={{ margin: '8px 0 10px 18px', padding: 0, fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                  {photosMarkedForRemoval.length > 0 ? (
                    <li>Remove {photosMarkedForRemoval.length} photo{photosMarkedForRemoval.length === 1 ? '' : 's'}</li>
                  ) : null}
                  {videoMarkedForRemoval ? <li>Remove the intro video</li> : null}
                  {activeFieldIssues.map((issue) => {
                    const cat = FIELD_ISSUE_CATEGORIES.find((c) => c.id === issue.category);
                    return (
                      <li key={`summary-${issue.category}`}>
                        {cat?.label || issue.category}{issue.note ? `: ${issue.note}` : ''}
                      </li>
                    );
                  })}
                </ul>
                <textarea
                  value={mediaChangeNote}
                  onChange={(event) => setMediaChangeNote(event.target.value)}
                  placeholder="Optional overall note to the owner"
                  data-testid="admin-approving-detail-media-change-note"
                  style={{ width: '100%', minHeight: 64, padding: 10, borderRadius: 10, border: '1px solid #E7E7E7', fontFamily: 'inherit', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={mediaChangeSubmitting}
                    onClick={async () => {
                      if (!onRequestMediaChanges) return;
                      setMediaChangeSubmitting(true);
                      try {
                        await onRequestMediaChanges(selectedVenueApp.id, {
                          removed_photos: photosMarkedForRemoval,
                          remove_video: videoMarkedForRemoval,
                          note: mediaChangeNote.trim(),
                          field_issues: activeFieldIssues,
                        });
                        setPhotosMarkedForRemoval([]);
                        setVideoMarkedForRemoval(false);
                        setMediaChangeNote('');
                        setFieldIssueDrafts({});
                      } finally {
                        setMediaChangeSubmitting(false);
                      }
                    }}
                    data-testid="admin-approving-detail-media-change-submit"
                    style={{ padding: '10px 14px', borderRadius: 12, border: 'none', background: mediaChangeSubmitting ? '#999' : '#DD0608', color: '#FFF', cursor: mediaChangeSubmitting ? 'wait' : 'pointer', fontWeight: 700 }}
                  >
                    {mediaChangeSubmitting ? 'Sending…' : 'Send for revision & notify owner'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPhotosMarkedForRemoval([]);
                      setVideoMarkedForRemoval(false);
                      setMediaChangeNote('');
                      setFieldIssueDrafts({});
                    }}
                    data-testid="admin-approving-detail-media-change-cancel"
                    style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', fontWeight: 700 }}
                  >
                    Clear selection
                  </button>
                </div>
              </div>
            ) : null}
          </Card>
        </>
      )}
    </div>
  );
}
